import React from "react";
import { Box } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { COLORS } from "../../../assets/theme";
import CustomTable from "../../../components/table/CustomTable";
import SearchHeaderContainer from "../../../components/table/SearchHeaderContainer";
import CustomLabel from "../../../components/CustomLabel";
import PageHeader from "../../../components/PageHeader";
import { useParams } from "react-router-dom";
import { getDailyRevenue } from "../../../services/newReportsService";
import { getAllBranches } from "../../../services/BranchesService";

const RevenueReport: React.FC = () => {
  const params = useParams<{ businessId?: string; id?: string }>();
  const businessId = params.businessId || params.id;

  if (!businessId) {
    throw new Error("Revenue report requires businessId in route");
  }

  const [rows, setRows] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [units, setUnits] = React.useState<any[]>([]);
  const [selectValue, setSelectValue] = React.useState<string | number>("all");

  const [dateRange, setDateRange] = React.useState<
    { start?: string; end?: string } | null
  >(() => {
    const today = new Date();
    const formatLocalDate = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    return { start: formatLocalDate(today), end: formatLocalDate(today) };
  });

  const parseDateInput = (value: any): Date | null => {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }

    const str = String(value).trim();
    if (!str) return null;

    const isoDateOnly = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoDateOnly) {
      const [, y, m, d] = isoDateOnly;
      const parsed = new Date(Number(y), Number(m) - 1, Number(d));
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const slashDate = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (slashDate) {
      let [, a, b, y] = slashDate;
      let month = Number(a);
      let day = Number(b);
      if (month > 12) {
        day = month;
        month = Number(b);
      }
      const parsed = new Date(Number(y), month - 1, day);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const parsed = new Date(str);
    if (!Number.isNaN(parsed.getTime())) {
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
    return null;
  };

  const formatLocalDate = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const normalizeDateInput = (value: any): string | undefined => {
    const parsed = parseDateInput(value);
    return parsed ? formatLocalDate(parsed) : undefined;
  };

  const toUtcIso = (date: Date | null, endOfDay = false) => {
    if (!date) return "";
    const y = date.getFullYear();
    const m = date.getMonth();
    const d = date.getDate();
    const utcDate = new Date(
      Date.UTC(y, m, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0)
    );
    return utcDate.toISOString();
  };

  const getUnitId = (u: any) => u.id ?? u.unit_id;

  React.useEffect(() => {
    const fetchUnits = async () => {
      try {
        const res = await getAllBranches(businessId);
        const unitsData = res?.units || res?.data || res || [];
        setUnits(Array.isArray(unitsData) ? unitsData : []);
      } catch {
        setUnits([]);
      }
    };

    fetchUnits();
  }, [businessId]);

  React.useEffect(() => {
    const fetchRevenue = async () => {
      setLoading(true);

      try {
        const startDate = parseDateInput(dateRange?.start);
        const endDate = parseDateInput(dateRange?.end);

        const params: any = {
          businessId,
          from: toUtcIso(startDate, false),
          to: toUtcIso(endDate, true),
        };

        if (selectValue !== "all") {
          params.unitId = String(selectValue);
        }

        const res = await getDailyRevenue(params);
        const data = Array.isArray(res) ? res : [];

        const mapped = data.map((r: any) => ({
          date: r.date,
          revenue: r.revenue,
        }));

        setRows(mapped);
      } catch (err) {
        console.error("Failed to fetch revenue:", err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRevenue();
  }, [businessId, selectValue, dateRange]);

  const exportCsv = React.useCallback(() => {
    if (!rows.length) return;

    const keys = ["date", "revenue"];
    const header = keys.join(",");
    const lines = rows.map((r) => {
      const row: Record<string, any> = {
        date: r.date ?? "-",
        revenue: r.revenue ?? "-",
      };
      return keys.map((k) => `"${String(row[k] ?? "-").replace(/"/g, '""')}"`).join(",");
    });

    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `revenue-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [rows]);

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <PageHeader
        title="Revenue Report"
        subtitle="Revenue for the selected period."
        sx={{ mt: { xs: 2, sm: 3 } }}
      />

      <SearchHeaderContainer
        search={search}
        setSearch={setSearch}
        dateRangeConfig={{
          value: dateRange ?? undefined,
          onChange: (v: any) => {
            if (Array.isArray(v)) {
              setDateRange({
                start: normalizeDateInput(v[0]),
                end: normalizeDateInput(v[1]),
              });
            } else if (v && (v.start || v.end)) {
              setDateRange({
                start: normalizeDateInput((v as any).start),
                end: normalizeDateInput((v as any).end),
              });
            } else {
              setDateRange(null);
            }
          },
        }}
        selectConfig={{
          options: [
            { label: "All", value: "all" },
            ...units.map((u) => ({
              label: u.name || "Unknown Branch",
              value: getUnitId(u),
            })),
          ],
          value: selectValue,
          onChange: (v: any) => setSelectValue(v),
          label: "Select Branch",
        }}
        buttonConfig={[
          {
            label: "Export CSV",
            onClick: () => Promise.resolve(exportCsv()),
            startIcon: <FileDownloadIcon />,
            bgColor: COLORS.MainColor,
            textColor: "#fff",
          },
        ]}
      />

      <CustomTable<any>
        filters={{ page: 0, size: 10, search }}
        clientSidePagination
        loading={loading}
        fields={[
          { key: "date", header: "Date" },
          { key: "revenue", header: "Revenue" },
        ]}
        fetchFunc={async (params?: any) => {
          const q = String(params?.search || search || "")
            .toLowerCase()
            .trim();

          const filtered = rows.filter((r) => {
            if (q) {
              const text = `${r.date} ${r.revenue}`.toLowerCase();
              if (!text.includes(q)) return false;
            }

            if (dateRange) {
              const rowDate = parseDateInput(r.date);
              const startFilter = parseDateInput(dateRange.start);
              const endFilter = parseDateInput(dateRange.end);

              if (startFilter) startFilter.setHours(0, 0, 0, 0);
              if (endFilter) endFilter.setHours(23, 59, 59, 999);

              if (rowDate) {
                if (startFilter && rowDate < startFilter) return false;
                if (endFilter && rowDate > endFilter) return false;
              }
            }

            return true;
          });

          return {
            content: filtered,
            number: 0,
            size: params?.size || 10,
            totalElements: filtered.length,
            totalPages: Math.max(1, Math.ceil(filtered.length / (params?.size || 10))),
          };
        }}
      />

      <Box
        sx={{
          backgroundColor: COLORS.WhiteBackground,
          borderRadius: "0 0 10px 10px",
          padding: "16px",
          marginTop: "-10px",
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        <CustomLabel text="Total Revenue:" />
        <CustomLabel
          text={new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(rows.reduce((sum, r) => sum + Number(r.revenue || 0), 0))}
          fontWeight="bold"
          sx={{ color: COLORS.MainColor }}
        />
      </Box>
    </Box>
  );
};

export default RevenueReport;
